import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Clock, Instagram, ArrowRight } from "lucide-react";
import { useScrollReveal } from "@/hooks/useAnimations";
import { getImageUrl } from "@/config/images";
import IntroHero from "@/components/intro/IntroHero";
import BackgroundVideo from "@/components/BackgroundVideo";
import ComicPage from "@/components/ComicPage";
import SpotsRadar from "@/components/SpotsRadar";
import Splash from "@/components/splash/Splash";

const SPLASH_MS = 700; // the splash bursts before the title and photos come in
// in comic page order (ComicPage.jsx): the big panel first, the wide one third
const FOOD_PHOTOS = [
  "food/food-13.jpeg",
  "food/food-1.jpeg",
  "atmosphere/atmosphere-4.jpeg",
  "food/food-5.jpeg",
  { src: "food/food-6.jpeg", whole: true },
  { src: "food/web/food-12-plate.webp", focus: "50% 62%" }, // the burger in its pan and the fries
  "atmosphere/atmosphere-5.jpeg", // tall photo, tall panel
  { src: "food/web/food-11-burger.webp", focus: "58% 68%" }, // the burger and the fries on our paper
];

const HomePage = () => {
  
  // Scroll reveal refs
  const [manifestoRef, manifestoVisible] = useScrollReveal({ threshold: 0.2 });
  const [locationsRef, locationsVisible] = useScrollReveal({ threshold: 0.1 });
  const [instagramRef, instagramVisible] = useScrollReveal({ threshold: 0.2 });
  const [newsletterRef, newsletterVisible] = useScrollReveal({ threshold: 0.2 });

  // The food section: a yellow splash bursts behind the photos every time you come to them; the first time,
  // the title pops once it has burst, then the photos slam in. Whoever scrolls on to the end gets everything.
  const splashRef = useRef(null);
  const [splashes, setSplashes] = useState(0);
  useEffect(() => {
    const seen = new IntersectionObserver(([entry]) => entry.isIntersecting && setSplashes((n) => n + 1), {
      threshold: 0.15,
    });
    seen.observe(splashRef.current);
    return () => seen.disconnect();
  }, []);
  const foodEndRef = useRef(null);
  const [foodShown, setFoodShown] = useState(false);
  const showFood = useCallback(() => setFoodShown(true), []);
  useEffect(() => {
    if (!splashes || foodShown) return undefined;
    const timer = setTimeout(showFood, SPLASH_MS);
    return () => clearTimeout(timer);
  }, [splashes, foodShown, showFood]);
  useEffect(() => {
    if (foodShown) return undefined;
    const reached = new IntersectionObserver(([entry]) => entry.isIntersecting && showFood());
    reached.observe(foodEndRef.current);
    return () => reached.disconnect();
  }, [foodShown, showFood]);

  const manifesto = [
    "WE ARE NOT JUST A SHOP",
    "WE ARE PHILOSOPHY",
    "WE ARE STREET",
    "WE CREATE MEMORIES",
    "WE UNITE PEOPLE THROUGH FOOD",
    "WE ARE VROMIARA",
  ];

  // lat/lon (city level) place the blips on the GTA radar; zone = its name picture in public/images/spots
  const featuredSpots = [
    { city: "ΙΩΑΝΝΙΝΑ", zone: "ioannina", address: "Ανεξαρτησίας 15", status: "OG SPOT - EST 2015", og: true, lat: 39.665, lon: 20.853 },
    { city: "ΠΕΡΙΣΤΕΡΙ", zone: "peristeri", address: "Παναγή Τσαλδάρη & Ερυθραίας", status: "EST. 2021", lat: 38.015, lon: 23.691 },
    { city: "ΘΕΣΣΑΛΟΝΙΚΗ", zone: "thessaloniki", address: "Συγγρού 7", status: "EST. 2023", lat: 40.64, lon: 22.944 },
    { city: "Ν. ΦΙΛΑΔΕΛΦΕΙΑ", zone: "nea-filadelfeia", address: "Βρυούλων 5", status: "EST. 2024", lat: 38.035, lon: 23.738 },
    { city: "ΑΛΕΞΑΝΔΡΟΥΠΟΛΗ", zone: "alexandroupoli", address: "Νικηφόρου Φωκά 7", status: "EST. 2025", lat: 40.847, lon: 25.874 },
    { city: "ΗΡΑΚΛΕΙΟ ΚΡΗΤΗΣ", zone: "heraklion", address: "Σαρανταπόρου 1", status: "EST. 2025", lat: 35.338, lon: 25.144 },
    { city: "ΚΟΜΟΤΗΝΗ", zone: "komotini", address: "Πλατεία Ειρήνης 33", status: "EST. 2026", lat: 41.122, lon: 25.406 },
    { city: "ΚΥΨΕΛΗ", zone: "kypseli", address: "Κασταλίας 8", status: "EST. 2026", lat: 37.999, lon: 23.737 },
  ];

  return (
    <div className="bg-black text-white" data-testid="home-page">
      <IntroHero />

      {/* Manifesto Section */}
      <section 
        ref={manifestoRef}
        className="py-24 bg-black relative overflow-hidden" 
        data-testid="manifesto-section"
      >
        {/* Background text watermark */}
        <div className="absolute inset-0 flex items-center justify-center overflow-hidden pointer-events-none select-none">
          <span className="text-[20vw] font-black text-white/[0.02] whitespace-nowrap">
            VROMIARA
          </span>
        </div>
        
        {/* a girl eating a Vromiara (AI clip made from our painted shots), behind the manifesto */}
        <BackgroundVideo
          src="videos/manifesto/girl.mp4"
          poster="videos/manifesto/girl-poster.webp"
          className="absolute inset-0 w-full h-full object-cover opacity-60"
          style={{ objectPosition: "45% 40%" }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/45 to-black/20" />
        <div className="absolute inset-0 bg-gradient-to-b from-black via-transparent to-black" />
        
        <div className="max-w-4xl mx-auto px-4 relative z-10">
          <div className={`space-y-4 transition-all duration-1000 ${manifestoVisible ? 'opacity-100' : 'opacity-0'}`}>
            {manifesto.map((line, index) => (
              <p
                key={index}
                className={`text-2xl md:text-4xl font-black tracking-wider transition-all duration-700 ${
                  index === manifesto.length - 1
                    ? "text-yellow-400 text-3xl md:text-5xl mt-8 text-shimmer"
                    : "text-white"
                }`}
                style={{
                  fontFamily: "'Bebas Neue', sans-serif",
                  textShadow: index === manifesto.length - 1 ? "0 0 30px rgba(255, 215, 0, 0.5)" : "none",
                  transform: manifestoVisible ? 'translateX(0)' : 'translateX(-30px)',
                  opacity: manifestoVisible ? 1 : 0,
                  transitionDelay: `${index * 100}ms`,
                }}
              >
                {line}
              </p>
            ))}
          </div>
          
          <div className={`mt-12 flex gap-4 flex-wrap transition-all duration-700 delay-700 ${manifestoVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <span className="tag-street bg-yellow-400/20 text-yellow-400 hover:bg-yellow-400/30">
              #VRMR
            </span>
            <span className="tag-street bg-pink-500/20 text-pink-500 hover:bg-pink-500/30">
              #SOUSVIDE
            </span>
            <span className="tag-street bg-yellow-400/20 text-yellow-400 hover:bg-yellow-400/30">
              #EST2015
            </span>
          </div>
        </div>
      </section>

      {/* Featured Food Section */}
      <section 
        className="pt-16 pb-14 bg-black relative overflow-hidden" 
        data-testid="featured-section"
      >

        <div className="max-w-7xl mx-auto px-4 relative z-10">
          {/* 1. the title pops */}
          <div className={`food-title relative text-center mb-16 ${foodShown ? "food-title-in" : ""}`}>
            <img
              src={getImageUrl("logos/fack-your-diet-tonight.png")}
              alt="#F*ck Your Diet Tonight"
              className="relative mx-auto mb-4 w-full sm:w-[85%] md:w-[65%] h-auto"
            />
            <p className="relative text-gray-400 text-lg max-w-2xl mx-auto">
              16 ώρες σιγομαγειρεμένη πανσέτα • SOUS VIDE τεχνική • Σπιτικές σάλτσες
            </p>
          </div>

          {/* 2. the photos slam in as a GTA loading-screen comic page. Behind them the logo's splash splats
              out every time you come to them (just past the page, never down to the loading bar), then stays
              centred on screen while you scroll, breathing slowly */}
          <div className="relative">
            <div ref={splashRef} className="food-splash-zone" aria-hidden="true">
              <div className="food-splash-stage">
                {splashes > 0 && (
                  <div key={splashes} className="food-splash">
                    <Splash variant="logo" animate preserveAspectRatio="none" className="w-full h-full text-[#ECCE21]" />
                  </div>
                )}
              </div>
            </div>
            <ComicPage photos={FOOD_PHOTOS} play={foodShown} />
          </div>

          <div ref={foodEndRef} className={`text-center mt-12 transition-all duration-700 delay-1000 ${foodShown ? 'opacity-100' : 'opacity-0'}`}>
            <Link
              to="/gallery"
              className="inline-flex items-center gap-2 text-yellow-400 hover:text-pink-500 transition-colors font-bold underline-grow"
            >
              VIEW FULL GALLERY <ArrowRight size={20} className="animate-bounce-subtle" />
            </Link>
          </div>
        </div>
      </section>

      {/* Locations Preview */}
      <section 
        ref={locationsRef}
        className="py-24 bg-black relative overflow-hidden" 
        data-testid="locations-preview"
      >
        {/* behind it: the master at his counter, making Βρωμιάρες (the same loop as in the food section, so it
            is already loaded); darkened towards every side */}
        <BackgroundVideo
          src="videos/master/master.mp4"
          poster="videos/master/master-poster.webp"
          className="absolute inset-0 w-full h-full object-cover"
          style={{ objectPosition: "55% 40%" }}
        />
        <div className="absolute inset-0 spots-shade" />

        <div className="max-w-7xl mx-auto px-4 relative z-10">
          <div className={`text-center mb-16 transition-all duration-700 ${locationsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <h2 className="text-4xl md:text-6xl font-black text-white mb-4" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
              OUR <span className="gradient-text-animated">SPOTS</span>
            </h2>
            <p className="text-gray-400">
              Check the SOLD OUT sign & Instagram story before you come!
            </p>
          </div>

          {/* the spots as a GTA San Andreas radar and menu */}
          <div className={`transition-all duration-700 ${locationsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <SpotsRadar spots={featuredSpots} play={locationsVisible} />
          </div>

          <div className={`text-center mt-12 transition-all duration-700 delay-500 ${locationsVisible ? 'opacity-100' : 'opacity-0'}`}>
            <Link
              to="/locations"
              className="magnetic-btn bg-yellow-400 text-black px-8 py-4 font-bold text-lg hover:bg-pink-500 hover:text-white transition-all inline-block rounded pulse-neon"
            >
              VIEW ALL LOCATIONS
            </Link>
          </div>
        </div>
      </section>

      {/* Efood Order Banner */}
      <section className="py-12 relative">
        <div className="max-w-7xl mx-auto px-4">
          <div className="rounded-xl p-6 md:p-8 relative overflow-hidden border border-red-500/20" style={{ background: 'linear-gradient(135deg, rgba(180,40,35,0.15) 0%, rgba(220,60,50,0.10) 50%, rgba(255,100,80,0.08) 100%)' }}>
            <div className="absolute inset-0 bg-grid-pattern opacity-10" />
            <div className="absolute -top-10 -right-10 w-40 h-40 bg-white/10 rounded-full blur-2xl" />

            <div className="relative z-10 flex flex-col md:flex-row items-center gap-6 text-center md:text-left">
              <div className="flex-1">
                <h3 className="text-2xl md:text-3xl font-black text-white mb-3" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
                  ΠΑΡΑΓΓΕΙΛΕ ΤΩΡΑ!
                </h3>
                <p className="text-white/90 text-sm md:text-base leading-relaxed">
                  Βρες μας στο <strong>efood</strong> και παράγγειλε τα αγαπημένα σου στο σπίτι!
                </p>
              </div>
              <a
                href="https://www.e-food.gr/delivery/ioannina/i-vromiara-the-gourmet-street-food-6931472"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 bg-red-500/20 text-white border border-red-500/30 px-8 py-4 rounded-lg font-black text-lg hover:bg-red-500/40 transition-all duration-300 magnetic-btn whitespace-nowrap"
              >
                <img
                  src="https://www.e-food.gr/favicon.ico"
                  alt="efood"
                  className="w-6 h-6"
                />
                EFOOD →
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Instagram CTA */}
      <section 
        ref={instagramRef}
        className="py-24 relative overflow-hidden" 
        data-testid="instagram-cta"
      >
        {/* Animated gradient background */}
        <div className="absolute inset-0 bg-gradient-to-r from-pink-600 via-purple-600 to-yellow-500 animate-gradient-shift" style={{ backgroundSize: '200% 200%' }} />
        
        {/* Overlay pattern */}
        <div className="absolute inset-0 bg-grid-pattern opacity-10" />
        
        <div className={`max-w-4xl mx-auto px-4 text-center relative z-10 transition-all duration-700 ${instagramVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}>
          <div className="inline-block p-4 bg-white/10 rounded-full backdrop-blur-sm mb-6 animate-float">
            <Instagram size={48} className="text-white" />
          </div>
          <h2 className="text-4xl md:text-6xl font-black text-white mb-4" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
            FOLLOW THE <span className="gradient-text-animated">VIBE</span>
          </h2>
          <p className="text-white/80 mb-8 text-lg">
            Check our Instagram for daily sold out updates, behind the scenes & more!
          </p>
          <a
            href="https://instagram.com/vromiarastreetfood"
            target="_blank"
            rel="noopener noreferrer"
            className="magnetic-btn bg-white text-black px-8 py-4 font-bold text-lg hover:bg-yellow-400 transition-all inline-flex items-center gap-2 rounded elevation-3"
            data-testid="instagram-follow-btn"
          >
            <Instagram size={20} />
            @VROMIARASTREETFOOD
          </a>
        </div>
      </section>

      {/* Newsletter Section */}
      <section 
        ref={newsletterRef}
        className="py-24 bg-black relative" 
        data-testid="newsletter-section"
      >
        {/* Subtle radial gradient */}
        <div className="absolute inset-0 bg-gradient-radial from-yellow-400/5 via-transparent to-transparent" />
        
        <div className={`max-w-2xl mx-auto px-4 text-center relative z-10 transition-all duration-700 ${newsletterVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="inline-block mb-6">
            <span className="badge-yellow animate-bounce-subtle">✨ Newsletter</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-black text-white mb-4" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
            ΚΑΝΕ ΕΓΓΡΑΦΗ ΝΑ ΤΑ ΜΑΘΑΙΝΕΙΣ ΟΛΑ <span className="gradient-text">ΣΤΑ ΜΠΑΜ</span>
          </h2>
          <p className="text-gray-400 mb-8">
            New spots, special drops, exclusive offers - be the first to know!
          </p>
          <form className="flex flex-col sm:flex-row gap-4" data-testid="newsletter-form">
            <input
              type="email"
              placeholder="Your email..."
              className="input-dark flex-1"
              data-testid="newsletter-email"
            />
            <button
              type="submit"
              className="magnetic-btn bg-yellow-400 text-black px-8 py-3 font-bold hover:bg-pink-500 hover:text-white transition-all rounded pulse-neon"
              data-testid="newsletter-submit"
            >
              SUBSCRIBE
            </button>
          </form>
          <p className="text-gray-600 text-xs mt-4">
            No spam, just good vibes. Unsubscribe anytime.
          </p>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
